import { useEffect, useState } from 'react';
import { Col, Form as AntForm, Row, Select } from 'antd';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import {
  getBatchClassesForSelect,
  getBatchesForSelect,
  getCourseSemestersForSelect,
  getCoursesForSelect,
} from '@functions/dataFormatters';
import { getSubjectsVariables, TimetableConfigurationProps } from '@modules/timetable';
import {
  BATCHES_FOR_TIMETABLE_SELECT,
  batches_for_timetable_selectQuery,
  batches_for_timetable_selectQueryVariables,
  COURSES_FOR_TIMETABLE_SELECT,
  courses_for_timetable_selectQuery,
} from '@app-types/graphql-types';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useMst } from '@models/Root';
import { HSMSContentCard, HSMSSelectOption } from '@components/shared';
import { themeConfig } from '@theme/index';

const { Option } = Select;
const { Item: FormItem } = AntForm;

const TimetableConfigurationCard = styled(HSMSContentCard)`
  .timetable-configuration-main-row {
    margin-bottom: -15px;
  }
`;

export const TimetableConfiguration = observer(
  ({
    courseId,
    setCourseId,
    courseSemesterId,
    setCourseSemesterId,
    getSubjects,
    subjectData,
    getAcademicCalendarItems,
  }: TimetableConfigurationProps) => {
    const { setClassId, classId, setBatch, batch, setInitialTimetableSlotCounts, setSelectedDraggableItem, week } =
      useMst().timetable;

    const [semestersForSelect, setSemestersForSelect] = useState<HSMSSelectOption[]>([]);
    const [selectedBatchId, setSelectedBatchId] = useState<string>('');
    const { data: coursesData, loading: coursesLoading } = useQuery<courses_for_timetable_selectQuery>(
      COURSES_FOR_TIMETABLE_SELECT,
      {
        fetchPolicy: 'no-cache',
      },
    );

    const [getBatches, { data: batchData, loading: batchLoading }] = useLazyQuery<
      batches_for_timetable_selectQuery,
      batches_for_timetable_selectQueryVariables
    >(BATCHES_FOR_TIMETABLE_SELECT, {
      fetchPolicy: 'no-cache',
    });

    const coursesForSelect: HSMSSelectOption[] = coursesData?.course ? getCoursesForSelect(coursesData.course) : [];
    const batchesForSelect: HSMSSelectOption[] = batchData?.active_batch
      ? getBatchesForSelect(batchData.active_batch)
      : [];
    const batchClassesForSelect: HSMSSelectOption[] = getBatchClassesForSelect(batch?.batch_classes);

    // Set initial subjectSlotCount to Mst when select a batch
    useEffect(() => {
      setInitialTimetableSlotCounts({ subjectData });
    }, [subjectData, batch?.id, setInitialTimetableSlotCounts, week?.toISOString()]);

    return (
      <TimetableConfigurationCard id="timetable-batch-class">
        <Row gutter={themeConfig.grid.formInputRowGroupGutter} className="timetable-configuration-main-row">
          <Col md={12} lg={6}>
            <FormItem labelCol={{ span: 24 }} label="Course">
              <Select
                value={courseId}
                loading={coursesLoading}
                style={{ display: 'inline-block' }}
                onSelect={(value) => {
                  setCourseId(value);
                  setCourseSemesterId('');
                  setBatch(null);
                  setClassId('');
                  setSelectedDraggableItem();

                  const course = coursesData?.course.find((courseData) => courseData.id === value);

                  getSubjects(getSubjectsVariables({}));

                  getBatches({
                    variables: {
                      where: {
                        courseId: {
                          _eq: value,
                        },
                      },
                    },
                  });
                  if (value && course && course.course_semesters) {
                    setSemestersForSelect(getCourseSemestersForSelect(course.course_semesters));
                  }
                }}
              >
                {coursesForSelect.map((course) => (
                  <Option key={course.value} value={course.value}>
                    {course.label}
                  </Option>
                ))}
              </Select>
            </FormItem>
          </Col>

          <Col md={12} lg={6}>
            <FormItem labelCol={{ span: 24 }} label="Semester">
              <Select
                value={courseSemesterId}
                style={{ width: '100%' }}
                onSelect={(semesterId) => {
                  setCourseSemesterId(semesterId);
                  setClassId('');
                  setSelectedDraggableItem();

                  if (semesterId && batch?.id) {
                    getSubjects(getSubjectsVariables({ batchId: batch?.id, semesterId }));
                  }
                  if (selectedBatchId && semesterId) {
                    getAcademicCalendarItems({
                      variables: {
                        where: { batchId: { _eq: selectedBatchId }, semester: { _eq: courseSemesterId } },
                      },
                    });
                  }
                }}
              >
                {semestersForSelect.map((semester) => (
                  <Option key={semester.value} value={semester.value}>
                    {semester.label}
                  </Option>
                ))}
              </Select>
            </FormItem>
          </Col>

          <Col md={12} lg={6}>
            <FormItem labelCol={{ span: 24 }} label="Batch">
              <Select
                value={batch?.id}
                style={{ width: '100%' }}
                loading={batchLoading}
                onSelect={(batchId) => {
                  if (batchId && courseSemesterId) {
                    getSubjects(getSubjectsVariables({ batchId, semesterId: courseSemesterId }));
                    setSelectedBatchId(batchId as string);
                  }

                  const selectedBatchData = batchData?.active_batch.find(
                    (singleBatchData) => singleBatchData.id === batchId,
                  );

                  if (selectedBatchData) {
                    setBatch(selectedBatchData);
                    setClassId('');
                  }

                  if (courseSemesterId) {
                    getAcademicCalendarItems({
                      variables: {
                        where: { batchId: { _eq: batchId }, semester: { _eq: courseSemesterId } },
                      },
                    });
                  }
                }}
              >
                {batchesForSelect.map((batchDataForSelect) => (
                  <Option key={batchDataForSelect.value} value={batchDataForSelect.value}>
                    {batchDataForSelect.label}
                  </Option>
                ))}
              </Select>
            </FormItem>
          </Col>

          <Col md={12} lg={6}>
            <FormItem labelCol={{ span: 24 }} label="Class">
              <Select
                value={classId}
                style={{ width: '100%' }}
                loading={batchLoading}
                onChange={(value) => {
                  setClassId(value);
                }}
                listItemHeight={100}
                options={batchClassesForSelect}
              />
            </FormItem>
          </Col>
        </Row>
      </TimetableConfigurationCard>
    );
  },
);
